import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const CURRENT_YEAR = new Date().getFullYear();

// 전자·전기·계기 과목은 이름 앞에 번개 표시를 붙인다
function isElectricSubject(subject) {
  const name = subject || '';
  return name.includes('전자') || name.includes('전기') || name.includes('계기');
}

// 과목명에 맞는 작은 아이콘 (없으면 기본 책 아이콘)
function subjectIcon(subject) {
  const name = subject || '';
  if (name.includes('법규')) return '📖';
  if (name.includes('일반')) return '⚙️';
  if (name.includes('기체')) return '✈️';
  if (name.includes('발동기')) return '🚀';
  if (isElectricSubject(name)) return '⚡';
  if (name.includes('시험')) return '📝';
  return '📘';
}

function hoursText(thry, prac) {
  return `${thry ?? 0}/${prac ?? 0}`;
}

// rows를 subject 기준으로 묶는다 (처음 나온 순서 유지)
function groupBySubject(rows) {
  const groups = [];
  const byName = new Map();
  rows.forEach((r) => {
    const key = r.subject || '(과목 없음)';
    let g = byName.get(key);
    if (!g) {
      g = { subject: key, items: [] };
      byName.set(key, g);
      groups.push(g);
    }
    g.items.push(r);
  });
  return groups;
}

export default function MobileMySubjects() {
  const [showAll, setShowAll] = useState(false); // false: 담당과목만, true: 전체과목
  const [amtRows, setAmtRows] = useState([]); // 항공안전법 지정교과목 (amtcs)
  const [catalog, setCatalog] = useState([]); // 표준교재 전체 챕터(PART) + 지정과목 코드
  const [assignments, setAssignments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openTop, setOpenTop] = useState({});
  const [openBottom, setOpenBottom] = useState({});
  const [openParts, setOpenParts] = useState({});
  const [sectionsByMat, setSectionsByMat] = useState({});
  const [selected, setSelected] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/admin/subjects'),
      api.get('/materials/catalog'),
      api.get('/teacher/assignments/me', { params: { year: CURRENT_YEAR } }),
    ])
      .then(([subjects, materials, mine]) => {
        // 스마트폰 담당과목은 비행기 과정만 보여준다 (회전익/항공전자 전용 항목 제외)
        const flightRows = subjects.data.filter((a) =>
          a.fxw !== undefined ? !!a.fxw : (a.fthrytime || 0) + (a.fpractime || 0) > 0
        );
        const flightCodes = new Set(flightRows.map((a) => a.amtcode));
        const allCodes = new Set(subjects.data.map((a) => a.amtcode));
        setAmtRows(flightRows);
        setCatalog(materials.data.filter((c) => !c.amtcode || !allCodes.has(c.amtcode) || flightCodes.has(c.amtcode)));
        setAssignments(mine.data);
      })
      .finally(() => setLoaded(true));
  }, []);

  const amtByCode = useMemo(() => new Map(amtRows.map((a) => [a.amtcode, a])), [amtRows]);
  const assignedMats = useMemo(() => new Set(assignments.map((a) => a.crsmatno)), [assignments]);

  // 담당 = 배정받은 표준교재 PART. 지정교과목은 그 PART에 연결된 지정과목 코드로 맞춘다.
  // (체크 해제: 배정된 PART/교육내용만, 체크: 전체)
  const asnByMat = useMemo(() => new Map(assignments.map((a) => [a.crsmatno, a.asnid])), [assignments]);
  const assignedAmtCodes = useMemo(
    () => new Set(catalog.filter((c) => assignedMats.has(c.crsmatno) && c.amtcode).map((c) => c.amtcode)),
    [catalog, assignedMats]
  );

  const topGroups = useMemo(
    () => groupBySubject(showAll ? amtRows : amtRows.filter((a) => assignedAmtCodes.has(a.amtcode))),
    [amtRows, showAll, assignedAmtCodes]
  );

  const bottomGroups = useMemo(
    () => groupBySubject(showAll ? catalog : catalog.filter((c) => assignedMats.has(c.crsmatno))),
    [catalog, showAll, assignedMats]
  );

  const toggleTop = (subject) => {
    setSelected(`t:${subject}`);
    setOpenTop((prev) => ({ ...prev, [subject]: !prev[subject] }));
  };

  const toggleBottom = (subject) => {
    setSelected(`b:${subject}`);
    setOpenBottom((prev) => ({ ...prev, [subject]: !prev[subject] }));
  };

  // 담당 보기에서는 섹션 단위로 배정받은 경우 배정된 섹션만, 전체 보기에서는 PART의 모든 섹션을 보여준다
  const sectionKey = (crsmatno) => `${showAll ? 'all' : 'mine'}:${crsmatno}`;

  const togglePart = (crsmatno) => {
    setOpenParts((prev) => ({ ...prev, [crsmatno]: !prev[crsmatno] }));
    const key = sectionKey(crsmatno);
    if (!sectionsByMat[key]) {
      const asnid = !showAll ? asnByMat.get(crsmatno) : undefined;
      api
        .get(`/materials/${crsmatno}/sections`, { params: asnid ? { asnid } : {} })
        .then((res) => setSectionsByMat((prev) => ({ ...prev, [key]: res.data })))
        .catch(() => setSectionsByMat((prev) => ({ ...prev, [key]: [] })));
    }
  };

  const emptyText = !loaded
    ? '불러오는 중...'
    : showAll
      ? '등록된 과목이 없습니다.'
      : '배정받은 담당과목이 없습니다. 오른쪽 위 "전체과목"을 체크하면 모든 과목을 볼 수 있습니다.';

  return (
    <div className="page mobile-mysubjects-page">
      <h2 className="mob-ms-title">담당과목</h2>

      <div className="mob-ms-head">
        <span className="mob-ms-label">항공안전법 지정교과목</span>
        <label className="mob-ms-all">
          전체과목
          <input type="checkbox" checked={showAll} onChange={(e) => {
              setShowAll(e.target.checked);
              setOpenParts({}); // 보기 방식이 바뀌면 열어 둔 PART(섹션)를 접는다
            }} />
        </label>
      </div>
      <div className="mob-tree">
        {topGroups.map((g) => {
          const open = !!openTop[g.subject];
          return (
            <div key={g.subject}>
              <button
                type="button"
                className={`mob-tree-row mob-tree-subject${selected === `t:${g.subject}` ? ' selected' : ''}`}
                onClick={() => toggleTop(g.subject)}
              >
                <span className="mob-tree-caret">{open ? '▾' : '▸'}</span>
                <span className="mob-tree-icon">{subjectIcon(g.subject)}</span>
                <span className="mob-tree-name">{g.subject}</span>
              </button>
              {open &&
                g.items.map((r) => (
                  <div key={r.amtcode} className="mob-tree-row mob-tree-leaf">
                    <span className="mob-tree-square" />
                    <span className="mob-tree-name">{r.chapter}</span>
                    <span className="mob-tree-hours">{hoursText(r.fthrytime, r.fpractime)}</span>
                  </div>
                ))}
            </div>
          );
        })}
        {topGroups.length === 0 && <div className="mob-tree-empty">{emptyText}</div>}
      </div>

      <div className="mob-ms-head mob-ms-head-second">
        <span className="mob-ms-label">표준교재</span>
      </div>
      <div className="mob-tree">
        {bottomGroups.map((g) => {
          const open = !!openBottom[g.subject];
          return (
            <div key={g.subject}>
              <button
                type="button"
                className={`mob-tree-row mob-tree-subject${selected === `b:${g.subject}` ? ' selected' : ''}`}
                onClick={() => toggleBottom(g.subject)}
              >
                <span className="mob-tree-caret">{open ? '▾' : '▸'}</span>
                <span className="mob-tree-square" />
                <span className="mob-tree-name">{g.subject}</span>
                <span className="mob-tree-badge">{g.items.length} 개 PART</span>
              </button>
              {open &&
                g.items.map((c) => {
                  const partOpen = !!openParts[c.crsmatno];
                  const amt = c.amtcode ? amtByCode.get(c.amtcode) : null;
                  const sections = sectionsByMat[sectionKey(c.crsmatno)];
                  return (
                    <div key={c.crsmatno}>
                      <button type="button" className="mob-tree-row mob-tree-part" onClick={() => togglePart(c.crsmatno)}>
                        <span className="mob-tree-caret">{partOpen ? '▾' : '▸'}</span>
                        <span className="mob-tree-doc" />
                        <span className="mob-tree-name">
                          {c.chapter}
                          {showAll && assignedMats.has(c.crsmatno) && <span className="mob-tree-mine">담당</span>}
                        </span>
                        {amt && <span className="mob-tree-hours">{hoursText(amt.fthrytime, amt.fpractime)}</span>}
                      </button>
                      {partOpen && (
                        <div className="mob-tree-sections">
                          {!sections && <div className="mob-tree-section">불러오는 중...</div>}
                          {sections &&
                            sections.map((s) => (
                              <div key={s.crssectno} className="mob-tree-section">{s.sections}</div>
                            ))}
                          {sections && sections.length === 0 && <div className="mob-tree-section">등록된 섹션이 없습니다.</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}
        {bottomGroups.length === 0 && <div className="mob-tree-empty">{emptyText}</div>}
      </div>
    </div>
  );
}
